import React, { useState } from 'react';
import { Stack, Button, CircularProgress, Tooltip, Typography, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../shared/firebase';
import { formatDateForDisplay } from '../utils/dateUtils';

// Solo Witme entiende el parámetro sandbox; a Deudafix y Unifye se les manda
// un alta REAL e irreversible. El registro guardaba `sandbox: true` para todos
// y aquí se pintaba «· sandbox» siempre que el campo no fuera exactamente
// false, así que cesiones reales figuraban como pruebas. Se mira también el
// proveedor para que los registros ya guardados dejen de mentir sin tener que
// reescribirlos. Auditoría del 05/09.
const PROVEEDORES_CON_SANDBOX = ['witme'];
const esSandbox = (provider, rec) =>
  rec.sandbox === true && PROVEEDORES_CON_SANDBOX.includes(provider);

const LABELS = { deudafix: 'Deudafix', unifye: 'Unifye', witme: 'Witme' };

const isSuccess = (rec) =>
  !!rec && (rec.ok === true || rec.status === 'processed' || rec.status === 'enviado');

// Manual "send this case to the affiliate API" buttons. Each button reflects
// its own sent state (disabled + info once sent) — no separate log needed.
const ApiSendButtons = ({ collection, caseId, providers = [], initialSends = {} }) => {
  const [sends, setSends] = useState(initialSends || {});
  const [busy, setBusy] = useState(null);
  const [errors, setErrors] = useState({});

  const send = async (provider) => {
    setBusy(provider);
    setErrors((e) => ({ ...e, [provider]: null }));
    try {
      const call = httpsCallable(functions, 'sendAffiliateLead');
      const res = await call({ collection, caseId, provider });
      const data = res.data || {};
      setSends((s) => ({
        ...s,
        [provider]: {
          ok: data.ok,
          status: data.status,
          sentAt: data.sentAt,
          httpStatus: data.httpStatus,
          emailAdjusted: data.emailAdjusted,
          emailUsed: data.emailUsed,
          // Lo dice el servidor, que es quien sabe si el proveedor entiende
          // sandbox. Antes se ponía true a pelo para todos.
          sandbox: data.sandbox === true,
        },
      }));
      if (!data.ok) {
        setErrors((e) => ({
          ...e,
          [provider]: `Respuesta: ${data.status || 'error'} (HTTP ${data.httpStatus})`,
        }));
      }
    } catch (err) {
      setErrors((e) => ({ ...e, [provider]: err?.message || 'Error al enviar' }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
      {providers.map((provider) => {
        const rec = sends[provider];
        const ok = isSuccess(rec);
        const sending = busy === provider;
        const err = errors[provider];
        const label = LABELS[provider] || provider;

        return (
          <Box key={provider} sx={{ minWidth: 190 }}>
            <Tooltip
              title={
                err ||
                (ok ? `Enviado ${formatDateForDisplay(rec.sentAt)}` : `Enviar este caso a ${label}`)
              }
            >
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  color={ok ? 'success' : err ? 'error' : 'primary'}
                  disabled={sending || ok}
                  onClick={() => send(provider)}
                  startIcon={
                    sending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : ok ? (
                      <CheckCircleIcon />
                    ) : err ? (
                      <ErrorOutlineIcon />
                    ) : (
                      <SendIcon />
                    )
                  }
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  {ok
                    ? `${label} · enviado`
                    : sending
                    ? 'Enviando…'
                    : err
                    ? `${label} · reintentar`
                    : `Enviar a ${label}`}
                </Button>
              </span>
            </Tooltip>
            {ok && rec.sentAt && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'text.secondary', textAlign: 'center' }}
              >
                {formatDateForDisplay(rec.sentAt)}
                {esSandbox(provider, rec) ? ' · sandbox' : ''}
              </Typography>
            )}
            {ok && rec.emailAdjusted && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.25, color: 'warning.main', textAlign: 'center', fontWeight: 600 }}
              >
                Email enviado con "+"{rec.emailUsed ? `: ${rec.emailUsed}` : ''}
              </Typography>
            )}
            {err && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'error.main', textAlign: 'center' }}
              >
                {err}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default ApiSendButtons;
